<template>
  <div>
    <div class="text-muted small">{{activity.created_at | formatRelativeDate}}</div>
    <!--USER LINK-->
    <UserLink :user="activity.causer"/>

    <!--ACTIVITY LOG ACTION-->
    {{ $t('actions.' + $options.filters.slugify(activity.description)) }}

    <!--CREATED-->
    <div v-if="activity.description === 'created'">
      <div v-if="activity.subject">
        <span> {{ $t('processes.log.title') | lowerCase}}</span>
        <router-link
                :to="{ name: 'processes.view', params: { slug: activity.subject.slug }}"
        >
            {{activity.properties.name}}
        </router-link>
      </div>
      <div v-else>
        <span> {{ $t('processes.log.title') | lowerCase}}</span>
        <router-link
                :to="{ name: 'processes.view', params: { slug: activity.properties.slug }}"
        >
            {{activity.properties.name}}
        </router-link>
      </div>
    </div>

    <!--DELETED-->
    <div v-if="activity.description === 'deleted'">
      <div v-if="activity.subject">
          <span> {{ $t('processes.log.title') | lowerCase}}</span>
          <span class="text-info"> {{activity.properties.name}}</span>
      </div>
      <div v-else>
          <span> {{ $t('processes.log.title') | lowerCase}}</span>
          <span class="text-info"> {{activity.properties.name}}</span>
      </div>
    </div>

    <!--UPDATED-->
    <div v-if="activity.description === 'updated'">
      <div v-if="activity.subject">
        <span> {{ $t('processes.log.title') | lowerCase}}</span>
        <router-link
                :to="{ name: 'processes.view', params: { slug: activity.subject.slug }}"
        >

          <span v-if="activity.properties.attributes.name"> {{activity.properties.attributes.name}}</span>
          <span v-else-if="activity.properties.process"> {{activity.properties.process.name}}</span>
          <span v-else> {{activity.subject.name}}</span>
        </router-link>
      </div>

      <div v-else>
        <span> {{ $t('processes.log.title') | lowerCase}}</span>
        <router-link
                :to="{ name: 'processes.view', params: { slug: activity.properties.attributes.slug }}"
        >
          <span v-if="activity.properties.attributes.name"> {{activity.properties.attributes.name}}</span>
          <span v-if="activity.properties.process"> {{activity.properties.process.name}}</span>
        </router-link>
      </div>

      <div v-if="changes">
        <div class="mt-2 small">
          <span class="text-warning text-bold">
            {{ $t('activities.log.changes')}}
          </span>
        </div>
        <div v-for="(item, index) in activity.properties.attributes">

          <div v-if="item !== activity.properties.old[index]">
            <span class="small" v-if="(!isObject(item) && index !== 'process_group_id')">
              <span class="text-bold">{{makeTitle(index)}}</span>
              <span> {{ $t('activities.log.changed_to')}} </span>
              <span v-if="index === 'process_group_id'" class="text-bold">
                {{activity.properties.attributes['processGroup'].name}}
              </span>
              <span v-else-if="index === 'updated_at'" class="text-bold">
                {{item | formatDate}}
              </span>
              <span v-else class="text-bold">{{item}}</span>
            </span>

            <span class="small" v-if="(index === 'process_group_id' && item !== null)">
              <span class="text-bold">{{makeTitle(index)}}</span>
              <span> {{ $t('activities.log.changed_to')}} </span>
              <span v-if="(index === 'process_group_id' && activity.properties.attributes['processGroup'])"
                    class="text-bold"
              >
                {{activity.properties.attributes['processGroup'].name}}
              </span>
              <span v-else-if="index === 'updated_at'" class="text-bold">
                {{item | formatDate}}
              </span>
              <span v-else class="text-bold">{{item}}</span>
            </span>
            <span class="small" v-if="(index === 'process_group_id' && item == null)">
              <span class="text-bold">{{makeTitle(index)}}</span>
              <span> {{ $t('activities.log.removed')}} </span>
            </span>

            <div class="small mt-2" v-if="index === 'tags_translated'">
              <div v-for="data in removedTags(activity.properties)" class="mb-1">
                <span>
                  <span class="badge badge-info p-1">
                    {{data.name_translated}}
                  </span>
                  {{ $t('activities.log.tag_removed')}}
                </span>
              </div>
              <div v-for="data in addedTags(activity.properties)" class="mb-1">
                <span>
                  <span class="badge badge-info p-1">
                    {{data.name_translated}}
                  </span>
                  {{ $t('activities.log.tag_added')}}
                </span>
              </div>
            </div>

            <div class="small mt-2" v-if="index === 'users'">
              <div v-for="data in removedOwners(activity.properties)" class="mb-1">
                <span>
                  <span qid="profile-photo" v-b-tooltip.hover :title="data.name">
                    <Avatar :object="data" size="xs"></Avatar>
                  </span>
                  <UserLink :user="data"/> {{ $t('activities.log.owner_removed')}}
                </span>
              </div>
              <div v-for="data in addedOwners(activity.properties)" class="mb-1">
                <span>
                  <span qid="profile-photo" v-b-tooltip.hover :title="data.name">
                    <Avatar :object="data" size="xs"></Avatar>
                  </span>
                  <UserLink :user="data"/> {{ $t('activities.log.owner_added')}}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!--ADDED-->
    <div v-if="activity.description === 'added'">
      <span> {{ $t('activities.log.title') | lowerCase}}</span>
      <router-link
              :to="{ name: 'activities.view', params: { slug: activity.properties.slug }}"
      >
          {{activity.properties.name}}
      </router-link>
      <span>{{ $t('system.on')}}</span>

      <div v-if="activity.subject" class="d-inline">
        <span> {{ $t('processes.log.title') | lowerCase}}</span>
        <router-link
                :to="{ name: 'processes.view', params: { slug: activity.subject.slug }}"
        >
            {{activity.subject.name}}
        </router-link>
      </div>

      <div v-else>
        <span> {{ $t('processes.log.title') | lowerCase}}</span>
      </div>
    </div>

    <!--REMOVED-->
    <div v-if="activity.description === 'removed'">
      <span> {{ $t('activities.log.title') | lowerCase}}</span>
      <router-link
              :to="{ name: 'activities.view', params: { slug: activity.properties.slug }}"
      >
        {{activity.properties.name}}
      </router-link>
      <span>{{ $t('system.from')}}</span>

      <div v-if="activity.subject" class="d-inline">
        <span> {{ $t('processes.log.title') | lowerCase}}</span>
        <router-link
                :to="{ name: 'processes.view', params: { slug: activity.subject.slug }}"
        >
          {{activity.subject.name}}
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import UserLink from '@/components/user/Link.vue';
  import Avatar from '@/components/Avatar.vue';

  export default {
    name: 'Process',
    data: function () {
      return {
        activeNavbar: this.$store.state.activeNavbar,
        items: [],
        currentPage: 1,
        totalRows: 0,
      }
    },
    components: {
      UserLink,
      Avatar
    },
    props: {
      activity: {},
      changes: false
    },
    computed: {
      getNavType() {
        return this.$store.state.activeNavbar
      },
      getClient() {
        return this.$store.state.client
      },
    },
    watch: {},
    methods: {
      loadContent(page) {
      },
      isObject: function (object) {
        return typeof object == "object"
      },
      makeTitle(slug) {
        var words = slug.split('_');

        for (var i = 0; i < words.length; i++) {
          if(words[i] !== 'id'){
            var word = words[i];
            words[i] = word.charAt(0).toUpperCase() + word.slice(1);
          }else{
            words[i] = '';
          }

        }

        return words.join(' ');
      },
      removedTags : function(properties){

        let results = [];

        for(var i=0; i < properties.old.tags_translated.length; i++){

          let removed = true;

          for(var j=0; j < properties.attributes.tags_translated.length; j++) {

            if(properties.old.tags_translated[i].id === properties.attributes.tags_translated[j].id){
              removed = false;
            }
          }

          if(removed){
            results.push(properties.old.tags_translated[i]);
          }

        }


        return results
      },
      addedTags : function(properties){
        let results = [];

        for(var i=0; i < properties.attributes.tags_translated.length; i++){

          let added = true;

          for(var j=0; j < properties.old.tags_translated.length; j++) {

            if(properties.attributes.tags_translated[i].id === properties.old.tags_translated[j].id){
              added = false;
            }
          }

          if(added){
            results.push(properties.attributes.tags_translated[i]);
          }

        }


        return results
      },
      removedOwners : function(properties){

        let results = [];

        for(var i=0; i < properties.old.users.length; i++){

          let removed = true;

          for(var j=0; j < properties.attributes.users.length; j++) {

            if(properties.old.users[i].id === properties.attributes.users[j].id){
              removed = false;
            }
          }

          if(removed){
            results.push(properties.old.users[i]);
          }

        }


        return results
      },
      addedOwners : function(properties){
        let results = [];

        for(var i=0; i < properties.attributes.users.length; i++){

          let added = true;

          for(var j=0; j < properties.old.users.length; j++) {

            if(properties.attributes.users[i].id === properties.old.users[j].id){
              added = false;
            }
          }

          if(added){
            results.push(properties.attributes.users[i]);
          }

        }


        return results
      }
    },
    created() {
    }
  }
</script>

<style scoped lang="scss">

</style>
